// Home (tienda v5) — port de maquetas/tienda-v5-mobile-first.html.
// Usa los shared TopNav/Footer/BottomNav/MiniBar; el resto es page-specific con CSS
// scopeado bajo .tv5-page-home (prefijo). Los carruseles de PRODUCTOS traen datos REALES de la BD
// (fetchProductosTienda, RPC anon-safe con fotos del bucket) — antes eran emojis hardcodeados.
// Los KITS quedan hardcodeados (6 fijos, cards de texto, precios verificados vs DB). Preserva:
// marquee top, hero, carruseles con auto-scroll desktop, bottom-sheet, franjas, touch/reduced-motion.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TopNav, Footer, BottomNav, MiniBar, ScrollHintPill } from '../../components/tienda-v5';
import { TV5_ROUTES } from '../../components/tienda-v5/routes';
import { fetchProductosTienda, categoriaEmoji } from '../../lib/tiendaApi';
import { useCart } from '../../lib/CartContext';

const fmt = (n) => '$' + Number(n).toLocaleString('es-UY');

const KITS = [
  { codigo: 'solo', tier: 'Essential', nombre: 'Kit Solo', productos: '10 productos esenciales para vivir solo o en pareja', precio: 990, ahorro: 310, tachado: 1300 },
  { codigo: 'familiar', tier: 'Essential', nombre: 'Kit Familiar', productos: '14 productos para familia con niños. Cocina + baño + ropa', precio: 1490, ahorro: 410, tachado: 1900, destacado: true },
  { codigo: 'hogar_plus', tier: 'Essential', nombre: 'Kit Hogar+', productos: '18 productos para casa grande o familia numerosa', precio: 2490, ahorro: 610, tachado: 3100 },
  { codigo: 'duo', tier: '★ Premium', premium: true, nombre: 'Kit Dúo', productos: '10 productos premium con aromas exclusivos brasileños', precio: 1090, ahorro: 310, tachado: 1400 },
  { codigo: 'confort', tier: '★ Premium', premium: true, nombre: 'Kit Confort', productos: '14 productos premium para familia. Fragancias importadas', precio: 1680, ahorro: 470, tachado: 2150 },
  { codigo: 'exclusive', tier: '★ Premium', premium: true, nombre: 'Kit Exclusive', productos: '20 productos premium para casa grande. Máxima calidad', precio: 2690, ahorro: 710, tachado: 3400 },
];

// Normaliza un producto de fetchProductosTienda a la forma que consume CardProd + el carrito
// (con FOTO real del bucket; emoji de categoría como fallback si algún producto no tiene imagen).
// LimpackFlex — "llega hoy/mañana/el lunes" según el corte (16h lun-vie · sáb 10h · dom→lunes).
function cuandoFlex(d) {
  const day = d.getDay(), h = d.getHours() + d.getMinutes() / 60;
  if (day === 0) return { p: 'el lunes', hoy: false };
  if (day === 6) return h < 10 ? { p: 'hoy', hoy: true } : { p: 'el lunes', hoy: false };
  return h < 16 ? { p: 'hoy', hoy: true } : { p: 'mañana', hoy: false };
}

function toCard(p) {
  return {
    sku: p.sku,
    nombre: p.nombre,
    presentacion: p.presentacion,
    precio: p.precio,
    precio_unit: p.precio,   // el carrito (CartContext) lee precio_unit
    tachado: p.tachado,
    sale: p.sale,            // '-X%' | undefined (ahorro vs precio de mercado)
    badge: p.badge,          // 'top' si destacado
    imagen_url: p.imagen_url,
    emoji: categoriaEmoji(p.categoria),
  };
}

const SKEL = Array.from({ length: 6 });

// Card de producto — la card abre el bottom sheet; el "+" agrega al carrito real sin abrirlo.
// Con qty>0 se transforma en stepper [− N +] (mismo patrón que Catalogo.jsx). qty reactiva/cross-tab.
function CardProd({ p, onAdd }) {
  const { items, updateQty } = useCart();
  const qty = items.find((it) => it.sku === p.sku)?.cantidad ?? 0;
  const flex = cuandoFlex(new Date());
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleAdd = (e) => { stop(e); onAdd(p); };
  const handleDec = (e) => { stop(e); updateQty(p.sku, qty - 1); };
  return (
    <Link to={`${TV5_ROUTES.producto}/${p.sku}`} className={'card-prod' + (qty > 0 ? ' en-carrito' : '')} aria-label={p.nombre}>
      <div className="card-prod-img">
        {p.imagen_url
          ? <img className="card-prod-foto" src={p.imagen_url} alt={p.nombre} loading="lazy" />
          : <span className="card-prod-emoji">{p.emoji || '🧴'}</span>}
        {qty > 0 ? (
          <div className="card-qty-stepper" onClick={stop} role="group" aria-label={`Cantidad de ${p.nombre}`}>
            <button type="button" className="card-qty-btn" onClick={handleDec} aria-label={qty === 1 ? `Quitar ${p.nombre}` : `Quitar uno`}>−</button>
            <span className="card-qty-val" aria-live="polite">{qty}</span>
            <button type="button" className="card-qty-btn" onClick={handleAdd} aria-label={`Agregar otro ${p.nombre}`}>+</button>
          </div>
        ) : (
          <button type="button" className="btn-add-flotante" aria-label={`Agregar ${p.nombre}`} onClick={handleAdd}>+</button>
        )}
      </div>
      {(p.badge === 'top' || p.badge === 'nuevo') && (
        <div className={'card-prod-tag' + (p.badge === 'nuevo' ? ' nuevo' : '')}>{p.badge === 'top' ? '★ Top' : 'Nuevo'}</div>
      )}
      <div className="card-prod-nombre">{p.nombre}</div>
      <div className="card-prod-presentacion">{p.presentacion}</div>
      <div className="card-prod-precios">
        <span className="card-prod-precio">{fmt(p.precio)}</span>
        {p.tachado && <span className="card-prod-precio-tachado">{fmt(p.tachado)}</span>}
        {p.sale && <span className="card-prod-off">{p.sale.replace('-', '')} OFF</span>}
      </div>
      <span className={'card-prod-llega' + (flex.hoy ? '' : ' manana')}><span className="card-prod-llega-truck">🚚</span> Llega {flex.p}</span>
    </Link>
  );
}

function CardProdSkel() {
  return (
    <div className="card-prod card-prod-skel" aria-hidden="true">
      <div className="card-prod-img skel-box" />
      <div className="skel-line skel-line-lg" />
      <div className="skel-line skel-line-sm" />
      <div className="skel-line skel-line-md" />
    </div>
  );
}

export default function Home() {
  const [sheet, setSheet] = useState(null); // producto seleccionado
  const [sheetQty, setSheetQty] = useState(1);
  const [productos, setProductos] = useState(null); // null = cargando
  const mainRef = useRef(null);

  // Datos REALES de la tienda (RPC anon-safe con fotos). Un fetch por carga (cacheado en tiendaApi).
  // NOTA: las cards de kit NO llevan foto (las del bucket eran generadas por IA, no representan el
  // contenido real → Nico las sacó). Son cards de texto (tier/nombre/desc/precio/ahorro/CTA).
  useEffect(() => {
    let alive = true;
    fetchProductosTienda()
      .then((list) => { if (alive) setProductos(list); })
      .catch((e) => { if (alive) { console.error('[Home] fetch productos', e); setProductos([]); } });
    return () => { alive = false; };
  }, []);

  const loading = productos == null;

  // 4 carruseles con señales REALES de la BD. La RPC anon (tienda_get_productos) NO trae created_at
  // ni categoría 'combo', así que "recién sumados"/"combos" no tienen señal → se reemplazan por
  // "para toda la casa" (variedad por categoría) y "sumá para envío gratis" (más económicos).
  const cuando = cuandoFlex(new Date());

  const carruseles = useMemo(() => {
    const ps = productos || [];
    const favoritos = ps.filter((p) => p.destacado).slice(0, 8).map(toCard);
    const rebajas = [...ps].filter((p) => p.salePct > 0).sort((a, b) => b.salePct - a.salePct).slice(0, 12).map(toCard);
    const seen = new Set(); const variedad = [];
    for (const p of ps) { if (!seen.has(p.categoria)) { seen.add(p.categoria); variedad.push(p); } }
    const casa = variedad.slice(0, 12).map(toCard);
    const economicos = [...ps].sort((a, b) => a.precio - b.precio).slice(0, 12).map(toCard);
    return { favoritos, rebajas, casa, economicos };
  }, [productos]);

  // Vitrina por categoría que ROTA sola (NO es carrusel horizontal): cada ~30 s cambia de categoría +
  // productos con un fade suave. Arranca por "Ropa" (lavarropa; incluye Cottonmax, todos destacados).
  // Reduced-motion → queda fija en Ropa. Así el Home no se estanca siempre en los mismos productos.
  const VIT_CATS = [
    { cat: 'lavarropa', label: 'Ropa', emoji: '👕' },
    { cat: 'multiuso', label: 'Multiuso', emoji: '🧽' },
    { cat: 'cocina', label: 'Cocina', emoji: '🍳' },
    { cat: 'manos', label: 'Manos', emoji: '🧼' },
    { cat: 'papel', label: 'Papel', emoji: '🧻' },
    { cat: 'accesorios', label: 'Accesorios', emoji: '🧹' },
  ];
  const vitrina = useMemo(() => {
    const ps = productos || [];
    return VIT_CATS
      .map((c) => ({
        ...c,
        items: ps
          .filter((p) => p.categoria === c.cat)
          .sort((a, b) => (b.destacado ? 1 : 0) - (a.destacado ? 1 : 0) || a.precio - b.precio)
          .slice(0, 8)
          .map(toCard),
      }))
      .filter((g) => g.items.length >= 4);
  }, [productos]);

  const [vitIdx, setVitIdx] = useState(0);
  useEffect(() => {
    if (vitrina.length <= 1) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const t = setInterval(() => setVitIdx((i) => (i + 1) % vitrina.length), 30000);
    return () => clearInterval(t);
  }, [vitrina.length]);
  const vitActual = vitrina.length ? vitrina[vitIdx % vitrina.length] : null;

  // Home = vitrina corta: hero + destacados + kits. El catálogo COMPLETO (agrupado por categoría,
  // arranca por ropa) vive en /catalogo (Tienda), así los kits no quedan enterrados abajo de 100 productos.

  // Auto-scroll ambient REMOVIDO (Fable, 2026-08-18): el movimiento solo de los carruseles mareaba.
  // Ahora el grueso de la tienda es una grilla vertical escaneable (abajo), no carruseles que se mueven.

  useEffect(() => { document.body.style.overflow = sheet ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [sheet]);

  // Carrito real (CartContext): el "+" de la card y el CTA del sheet agregan de verdad + toast + bump.
  const { addItem } = useCart();
  const [toast, setToast] = useState('');
  const [bumpSignal, setBumpSignal] = useState(0);
  const toastTimer = useRef(null);
  const onAdd = (p, cant = 1) => {
    addItem(p, cant);
    setToast(`✓ ${p.nombre} agregado`);
    setBumpSignal((n) => n + 1);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2000);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function openSheet(p) { setSheet(p); setSheetQty(1); }

  return (
    <div className="tv5-page-home" ref={mainRef}>
      <style>{HOME_CSS}</style>

      <TopNav active="home" accent="teal" />

      {/* MARQUEE trust bar */}
      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <MarqueeItems key={dup} />
          ))}
        </div>
      </div>

      <main>
        <section className="hero">
          <div className="hero-flex-chip">
            <span className="hero-flex-bolt">⚡</span> LimpackFlex · llega <b style={{ color: cuando.hoy ? '#10B981' : '#5BBDD8' }}>{cuando.p}</b>
          </div>
          <h1>Comprá hoy, ahorrá hoy<br /><span className="hero-h1-g">y te llega hoy.</span></h1>
          <p>Productos de limpieza sueltos, sin suscripción ni mínimos. En Montevideo y Canelones (barrios privados también) e interior del país.</p>
          <div className="hero-cta-wrap">
            <Link to={TV5_ROUTES.tienda} className="hero-cta-primary hero-cta-revolver">
              Entrá a revolver →
              <span className="hero-cta-sub">entrá a la tienda</span>
            </Link>
            <a href="#kits" className="hero-cta-link">o suscribite a un kit y ahorrá más todavía →</a>
          </div>
        </section>

        {/* BLOQUE TIENDA (OneShot primero) */}
        <section className="bloque" id="tienda">
          <div className="bloque-header">
            <div className="bloque-header-titulo">
              <h2 className="bloque-titulo">Tienda</h2>
            </div>
            <Link to={TV5_ROUTES.tienda} className="btn-ver-todo">Ver la tienda <span className="arrow">→</span></Link>
          </div>
          <div className="franja-envio teal"><span className="franja-bolt">⚡</span> <span><strong>Gratis desde $1.000</strong> · Llega {cuando.p} si comprás antes de las 16 h</span></div>

          {loading ? (
            <>
              <div className="grid-titulo">&nbsp;</div>
              <div className="grid-prod">{SKEL.map((_, i) => <CardProdSkel key={i} />)}</div>
            </>
          ) : vitActual && (
            <div className="vitrina-rot" key={vitActual.cat}>
              <div className="grid-titulo"><span className="emoji">{vitActual.emoji}</span> {vitActual.label}</div>
              <div className="grid-prod">
                {vitActual.items.map((p) => <CardProd key={p.sku} p={p} onAdd={onAdd} />)}
              </div>
            </div>
          )}
          <div className="grid-vertodo">
            <Link to={TV5_ROUTES.tienda} className="btn-ver-todo">Ver toda la tienda <span className="arrow">→</span></Link>
          </div>
        </section>

        <div className="divisor"><span className="divisor-text">— O —</span></div>

        {/* BLOQUE KITS (suscripción segundo) */}
        <section className="bloque" id="kits">
          <div className="bloque-header">
            <div className="bloque-header-titulo">
              <div className="bloque-eyebrow gold">🎁 Suscripción mensual</div>
              <h2 className="bloque-titulo">¿Todos los meses? Suscribite y ahorrá</h2>
              <div className="bloque-subtitulo">Elegís, te suscribís, y te olvidás. Pasamos por tu casa cada mes.</div>
            </div>
            <Link to={TV5_ROUTES.kits} className="btn-ver-todo gold">Ver todos <span className="arrow">→</span></Link>
          </div>
          <div className="franja-envio green">🚚 <span>Pasamos por tu barrio · <strong>Sin costo de envío</strong></span></div>
          <div className="carrusel">
            {KITS.map((k) => (
              <div key={k.codigo} className={'card-kit' + (k.destacado ? ' destacado' : '')}>
                {k.destacado && <div className="badge-mas-pedido">El más pedido</div>}
                <div className="card-kit-tier" style={k.premium ? { color: 'var(--tv5h-gold)' } : undefined}>{k.tier}</div>
                <div className="card-kit-nombre">{k.nombre}</div>
                <div className="card-kit-productos">{k.productos}</div>
                <div className="card-kit-precio-row">
                  <div className="card-kit-precio">{fmt(k.precio)}</div>
                  <div className="card-kit-por-mes">/ mes</div>
                </div>
                <div className="card-kit-ahorro">Ahorrás {fmt(k.ahorro)}</div>
                <Link to={'/kit/' + k.codigo} className="card-kit-cta">Descubrí el kit</Link>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Toast "✓ agregado" (mismo lenguaje que Catalogo) */}
      <div className={'home-toast' + (toast ? ' visible' : '')} role="status" aria-live="polite">{toast}</div>

      <MiniBar checkoutHref={TV5_ROUTES.checkout} bumpSignal={bumpSignal} />
      <Footer accent="teal" />
      <BottomNav active="home" />

      {/* BOTTOM SHEET removido (Fable 2026-08-18): las cards ahora van a la ficha real (ProductoDetalle),
          no a un modal con descripción demo. */}
    </div>
  );
}

function MarqueeItems() {
  return (
    <>
      <span><b>⚡</b> LimpackFlex · llega hoy en Montevideo y Canelones</span>
      <span><b>✓</b> Comprá en la tienda ahorrando de verdad también</span>
      <span><b>✓</b> Sin suscripción ni mínimos</span>
      <span><b>✓</b> Mejor precio que el promedio del mercado</span>
      <span><b>✓</b> Cobros seguros con <span className="marquee-mp"><img src="/mp-logo.svg" alt="MercadoPago" /></span></span>
      <span><b>✓</b> Tu cuenta gratis · si precisás una mano, estamos</span>
      <span><b>✓</b> ¿Todos los meses? Suscribite y ahorrá más todavía</span>
    </>
  );
}

function CarruselProd({ titulo, emoji, items, loading, onOpen, onAdd }) {
  // Sin datos (fetch vacío/error) y sin loading → no renderizamos el carrusel (evita título huérfano).
  if (!loading && (!items || items.length === 0)) return null;
  return (
    <>
      <div className="carrusel-titulo">
        <div className="carrusel-titulo-label"><span className="emoji">{emoji}</span> {titulo}</div>
        <Link to={TV5_ROUTES.tienda} className="mini-ver">Ver todos</Link>
      </div>
      <div className="carrusel">
        {loading
          ? SKEL.map((_, i) => <CardProdSkel key={i} />)
          : items.map((p) => <CardProd key={p.sku} p={p} onOpen={onOpen} onAdd={onAdd} />)}
      </div>
    </>
  );
}

const HOME_CSS = `
.tv5-page-home {
  --tv5h-navy:#1A3461; --tv5h-teal:#00A8B5; --tv5h-gold:#C5A059; --tv5h-green:#10B981;
  --tv5h-bg:#0A1424; --tv5h-card:rgba(255,255,255,0.04); --tv5h-border:rgba(255,255,255,0.08);
  --tv5h-text:#F8F6F1; --tv5h-muted:rgba(248,246,241,0.65); --tv5h-dim:rgba(248,246,241,0.45);
  --safe-bottom:env(safe-area-inset-bottom,0px);
  background:var(--tv5h-bg); color:var(--tv5h-text); min-height:100vh; position:relative;
  overflow-x:clip; padding-bottom:calc(72px + var(--safe-bottom) + 68px);
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
}
.tv5-page-home::before { content:''; position:fixed; inset:0; z-index:0; pointer-events:none;
  background:radial-gradient(circle at 20% 10%, rgba(0,168,181,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 60%, rgba(197,160,89,0.08) 0%, transparent 40%); }
.tv5-page-home main { position:relative; z-index:1; }
.tv5-page-home a { color:inherit; }
@media (min-width:900px){ .tv5-page-home { padding-bottom:0; } }

/* MARQUEE */
.tv5-page-home .marquee { position:relative; z-index:1; border-bottom:1px solid var(--tv5h-border); overflow:hidden; padding:7px 0; background:rgba(0,0,0,0.25); }
.tv5-page-home .marquee-track { display:flex; gap:32px; white-space:nowrap; animation:tv5hMq 40s linear infinite; width:max-content; }
.tv5-page-home .marquee span { font-size:12px; font-weight:500; color:var(--tv5h-muted); display:flex; align-items:center; gap:6px; }
.tv5-page-home .marquee b { color:var(--tv5h-teal); font-weight:700; }
.tv5-page-home .marquee-mp { display:inline-flex; align-items:center; padding:2px 8px; background:#FFE600; border-radius:5px; margin-left:4px; }
.tv5-page-home .marquee-mp img { height:26px; display:block; }
@keyframes tv5hMq { from { transform:translateX(0); } to { transform:translateX(-50%); } }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .marquee-track { animation:none; } }
@media (min-width:900px){ .tv5-page-home .marquee-track { gap:48px; animation-duration:48s; } .tv5-page-home .marquee span { font-size:13px; } .tv5-page-home .marquee-mp img { height:28px; } }

/* HERO */
.tv5-page-home .hero { padding:30px 20px 6px; text-align:center; max-width:900px; margin:0 auto; }
.tv5-page-home .hero h1 { font-size:clamp(26px,6vw,44px); font-weight:800; letter-spacing:-0.03em; line-height:1.1; margin-bottom:10px; color:var(--tv5h-text); }
.tv5-page-home .hero h1 .hero-h1-g { color:#5BE7C0; }
.tv5-page-home .vitrina-rot { animation:tv5hVitFade 0.5s ease; }
@keyframes tv5hVitFade { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .vitrina-rot { animation:none; } }
.tv5-page-home .hero p { color:var(--tv5h-muted); font-size:14px; max-width:520px; margin:0 auto; line-height:1.5; }
.tv5-page-home .hero-precio { display:inline-flex; flex-wrap:wrap; justify-content:center; align-items:center; gap:6px; margin-bottom:14px; padding:7px 14px; background:rgba(0,168,181,0.12); border:1px solid rgba(0,168,181,0.35); border-radius:999px; font-size:12.5px; color:var(--tv5h-teal); font-weight:600; max-width:calc(100vw - 40px); line-height:1.3; }
.tv5-page-home .hero-precio b { color:var(--tv5h-text); font-weight:800; }
.tv5-page-home .hero-cta-wrap { display:flex; flex-direction:column; gap:12px; margin-top:16px; align-items:center; }
.tv5-page-home .hero-cta-primary { display:inline-flex; align-items:center; justify-content:center; gap:8px; background:linear-gradient(135deg, var(--tv5h-teal) 0%, #00C4D4 100%); color:var(--tv5h-navy); padding:15px 30px; border-radius:12px; font-weight:800; font-size:15px; text-decoration:none; box-shadow:0 10px 30px rgba(0,168,181,0.4); min-width:220px; min-height:48px; transition:transform 0.2s; }
.tv5-page-home .hero-cta-link { color:var(--tv5h-muted); font-size:13px; text-decoration:none; padding:8px 14px; border-radius:8px; transition:color 0.2s; }
.tv5-page-home .hero-cta-link:active { color:var(--tv5h-teal); }
.tv5-page-home .scroll-hint { display:none; margin:28px auto 0; color:var(--tv5h-dim); text-decoration:none; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-weight:600; flex-direction:column; align-items:center; gap:4px; opacity:0.7; animation:tv5hBounce 2.2s ease-in-out infinite; width:fit-content; }
.tv5-page-home .scroll-hint svg { display:block; }
@keyframes tv5hBounce { 0%,100% { transform:translateY(0); } 50% { transform:translateY(6px); } }
@media (max-width:899px){ .tv5-page-home .scroll-hint { display:flex; } }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .scroll-hint { animation:none; } }
@media (hover:hover){ .tv5-page-home .hero-cta-primary:hover { transform:translateY(-2px); } .tv5-page-home .hero-cta-link:hover { color:var(--tv5h-teal); } }
/* HERO LimpackFlex chip + CTA revolver */
.tv5-page-home .hero-flex-chip { display:inline-flex; flex-wrap:wrap; justify-content:center; align-items:center; gap:6px; margin-bottom:14px; padding:7px 14px; background:rgba(0,168,181,0.12); border:1px solid rgba(0,168,181,0.45); border-radius:999px; font-size:12.5px; color:var(--tv5h-teal); font-weight:700; max-width:calc(100vw - 40px); line-height:1.3; animation:tv5hFlexGlow 2.4s ease-in-out infinite; }
.tv5-page-home .hero-flex-chip b { font-weight:800; }
.tv5-page-home .hero-flex-bolt { display:inline-block; animation:tv5hFlexBlink 1.2s ease-in-out infinite; }
.tv5-page-home .hero-cta-revolver { flex-direction:column; gap:2px; padding:13px 30px; }
.tv5-page-home .hero-cta-sub { font-size:11px; font-weight:600; opacity:0.75; text-transform:none; letter-spacing:0; }
@keyframes tv5hFlexGlow { 0%,100% { box-shadow:0 0 0 0 rgba(0,168,181,0); } 50% { box-shadow:0 0 16px 2px rgba(0,168,181,0.35); } }
@keyframes tv5hFlexBlink { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .hero-flex-chip, .tv5-page-home .hero-flex-bolt { animation:none; } }

/* BLOQUE */
.tv5-page-home .bloque { padding:16px 20px 24px; max-width:1400px; margin:0 auto; scroll-margin-top:64px; }
.tv5-page-home .bloque-header { display:flex; align-items:flex-end; justify-content:space-between; gap:12px; margin-bottom:12px; }
.tv5-page-home .bloque-header-titulo { flex:1 1 auto; min-width:0; }
.tv5-page-home .bloque-eyebrow { font-size:10px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; margin-bottom:6px; display:inline-flex; align-items:center; gap:6px; }
.tv5-page-home .bloque-eyebrow.teal { color:var(--tv5h-teal); }
.tv5-page-home .bloque-eyebrow.gold { color:var(--tv5h-gold); }
.tv5-page-home .bloque-titulo { font-size:clamp(20px,5vw,32px); font-weight:800; letter-spacing:-0.02em; line-height:1.15; }
.tv5-page-home .bloque-subtitulo { color:var(--tv5h-muted); font-size:12px; margin-top:4px; }
.tv5-page-home .btn-ver-todo { background:linear-gradient(135deg, var(--tv5h-teal) 0%, #00C4D4 100%); border:0; color:var(--tv5h-navy); padding:9px 14px 9px 16px; border-radius:999px; font-size:12px; font-weight:800; cursor:pointer; text-decoration:none; display:inline-flex; align-items:center; gap:6px; white-space:nowrap; flex-shrink:0; box-shadow:0 8px 22px rgba(0,168,181,0.35), 0 2px 6px rgba(0,0,0,0.3); transition:all 0.25s cubic-bezier(0.34,1.56,0.64,1); }
.tv5-page-home .btn-ver-todo .arrow { display:inline-block; transition:transform 0.3s cubic-bezier(0.34,1.56,0.64,1); }
.tv5-page-home .btn-ver-todo.gold { background:linear-gradient(135deg, var(--tv5h-gold) 0%, #D4B370 100%); color:var(--tv5h-navy); box-shadow:0 8px 22px rgba(197,160,89,0.4), 0 2px 6px rgba(0,0,0,0.3); }
.tv5-page-home .btn-ver-todo:active { transform:translateY(-1px); }
@media (hover:hover){ .tv5-page-home .btn-ver-todo:hover { transform:translateY(-2px); box-shadow:0 12px 28px rgba(0,168,181,0.5); } .tv5-page-home .btn-ver-todo:hover .arrow { transform:translateX(6px); } }
@media (min-width:640px){ .tv5-page-home .btn-ver-todo { padding:11px 18px 11px 20px; font-size:13px; } }

/* FRANJA */
.tv5-page-home .franja-envio { display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; font-size:12px; font-weight:600; margin-bottom:18px; line-height:1.3; }
.tv5-page-home .franja-envio.green { background:rgba(16,185,129,0.1); border:1px solid rgba(16,185,129,0.3); color:#6EE7B7; }
.tv5-page-home .franja-envio.gold { background:rgba(197,160,89,0.1); border:1px solid rgba(197,160,89,0.3); color:var(--tv5h-gold); }
.tv5-page-home .franja-envio.teal { background:rgba(0,168,181,0.1); border:1px solid rgba(0,168,181,0.35); color:var(--tv5h-teal); }
.tv5-page-home .franja-envio .franja-bolt { display:inline-block; animation:tv5hFlexBlink 1.2s ease-in-out infinite; }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .franja-envio .franja-bolt { animation:none; } }

/* CARRUSEL */
.tv5-page-home .carrusel-titulo { display:flex; align-items:center; justify-content:space-between; gap:8px; margin:24px 0 10px; }
.tv5-page-home .carrusel-titulo:first-of-type { margin-top:8px; }
.tv5-page-home .carrusel-titulo-label { display:flex; align-items:center; gap:6px; font-size:15px; font-weight:700; color:var(--tv5h-text); }
.tv5-page-home .carrusel-titulo-label .emoji { font-size:18px; }
.tv5-page-home .carrusel-titulo a.mini-ver { color:var(--tv5h-teal); font-size:11px; font-weight:700; text-decoration:none; opacity:0.8; }
.tv5-page-home .carrusel { display:flex; gap:10px; overflow-x:auto; scroll-snap-type:x mandatory; -webkit-overflow-scrolling:touch; padding:0 20px 12px; margin:0 -20px; scrollbar-width:thin; scrollbar-color:var(--tv5h-teal) rgba(255,255,255,0.05); }
.tv5-page-home .carrusel::-webkit-scrollbar { height:4px; }
.tv5-page-home .carrusel::-webkit-scrollbar-thumb { background:var(--tv5h-teal); border-radius:3px; }

/* CARD KIT */
.tv5-page-home .card-kit { flex:0 0 78vw; max-width:320px; scroll-snap-align:start; background:linear-gradient(135deg, rgba(0,168,181,0.08) 0%, rgba(255,255,255,0.03) 100%); border:1px solid rgba(0,168,181,0.25); border-radius:16px; padding:18px; position:relative; overflow:hidden; transition:all 0.3s; cursor:pointer; }
@media (min-width:640px){ .tv5-page-home .card-kit { flex-basis:280px; } }
.tv5-page-home .card-kit:active { transform:translateY(-3px); border-color:var(--tv5h-teal); box-shadow:0 12px 32px rgba(0,168,181,0.2); }
.tv5-page-home .card-kit.destacado { border-color:var(--tv5h-teal); box-shadow:0 0 30px rgba(0,168,181,0.15); }
.tv5-page-home .badge-mas-pedido { position:absolute; top:12px; right:12px; z-index:2; background:var(--tv5h-teal); color:var(--tv5h-navy); font-size:9px; font-weight:800; letter-spacing:0.08em; padding:4px 8px; border-radius:999px; text-transform:uppercase; }
.tv5-page-home .card-kit-tier { font-size:10px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--tv5h-teal); margin-bottom:6px; }
.tv5-page-home .card-kit-nombre { font-size:22px; font-weight:800; letter-spacing:-0.02em; margin-bottom:10px; }
.tv5-page-home .card-kit-productos { color:var(--tv5h-muted); font-size:12px; margin-bottom:16px; line-height:1.5; }
.tv5-page-home .card-kit-precio-row { display:flex; align-items:baseline; gap:8px; margin-bottom:4px; }
.tv5-page-home .card-kit-precio { font-size:26px; font-weight:800; letter-spacing:-0.02em; }
.tv5-page-home .card-kit-por-mes { font-size:12px; color:var(--tv5h-muted); }
.tv5-page-home .card-kit-ahorro { display:inline-block; background:rgba(16,185,129,0.15); color:#6EE7B7; font-size:11px; font-weight:700; padding:4px 10px; border-radius:999px; margin-bottom:16px; }
.tv5-page-home .card-kit-ahorro-tachado { color:var(--tv5h-dim); font-size:10px; text-decoration:line-through; margin-left:4px; }
.tv5-page-home .card-kit-cta { display:block; background:linear-gradient(135deg, var(--tv5h-navy) 0%, var(--tv5h-teal) 100%); color:#fff; text-align:center; padding:12px 16px; border-radius:10px; font-weight:700; font-size:13px; text-decoration:none; transition:transform 0.2s; }
.tv5-page-home .card-kit-cta:active { transform:scale(0.97); }
@media (hover:hover){ .tv5-page-home .card-kit:hover { transform:translateY(-3px); border-color:var(--tv5h-teal); box-shadow:0 12px 32px rgba(0,168,181,0.2); } }

/* DIVISOR */
.tv5-page-home .divisor { display:flex; align-items:center; justify-content:center; padding:24px 20px; max-width:600px; margin:0 auto; }
.tv5-page-home .divisor::before, .tv5-page-home .divisor::after { content:''; flex:1; height:1px; background:linear-gradient(90deg, transparent, var(--tv5h-border), transparent); }
.tv5-page-home .divisor-text { color:var(--tv5h-muted); font-size:12px; font-weight:600; letter-spacing:0.2em; padding:0 20px; }

/* CARD PROD */
.tv5-page-home .card-prod { flex:0 0 44vw; max-width:220px; scroll-snap-align:start; background:var(--tv5h-card); border:1px solid var(--tv5h-border); border-radius:16px; padding:13px; position:relative; transition:all 0.3s; cursor:pointer; display:flex; flex-direction:column; }
@media (min-width:640px){ .tv5-page-home .card-prod { flex-basis:180px; } }
.tv5-page-home .card-prod:active { transform:translateY(-3px); border-color:var(--tv5h-gold); box-shadow:0 8px 24px rgba(197,160,89,0.15); }
.tv5-page-home .card-prod-img { aspect-ratio:1; background:#fff; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:48px; margin-bottom:11px; position:relative; overflow:hidden; }
.tv5-page-home .card-prod-foto { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
.tv5-page-home .card-prod-emoji { line-height:1; }
/* Mobile (≤639px, 2 columnas): en pantallas angostas la imagen cuadrada quedaba chica. La pasamos a
   3/4 (retrato) para que la foto del producto se vea más grande, y achicamos un poco el padding. */
@media (max-width:639px){
  .tv5-page-home .card-prod-img { aspect-ratio:3 / 4; margin-bottom:9px; }
  .tv5-page-home .card-prod { padding:10px; }
}
/* SKELETON de carga (shimmer) — mismo lenguaje que Catalogo.jsx */
.tv5-page-home .card-prod-skel { pointer-events:none; }
.tv5-page-home .skel-box, .tv5-page-home .skel-line { background:linear-gradient(100deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.04) 70%); background-size:200% 100%; animation:tv5hSkel 1.2s ease-in-out infinite; border-radius:8px; }
.tv5-page-home .skel-line { height:11px; margin-top:8px; }
.tv5-page-home .skel-line-lg { width:90%; }
.tv5-page-home .skel-line-sm { width:50%; }
.tv5-page-home .skel-line-md { width:40%; height:16px; margin-top:10px; }
@keyframes tv5hSkel { from { background-position:200% 0; } to { background-position:-200% 0; } }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .skel-box, .tv5-page-home .skel-line { animation:none; } }
.tv5-page-home .badge-sale { position:absolute; top:8px; right:8px; background:var(--tv5h-gold); color:var(--tv5h-navy); font-size:10px; font-weight:900; padding:4px 9px; border-radius:999px; box-shadow:0 4px 12px rgba(197,160,89,0.4); z-index:2; }
.tv5-page-home .badge-nuevo, .tv5-page-home .badge-top { position:absolute; top:8px; left:8px; font-size:9px; font-weight:800; letter-spacing:0.08em; padding:3px 7px; border-radius:4px; text-transform:uppercase; z-index:2; }
.tv5-page-home .badge-nuevo { background:var(--tv5h-teal); color:var(--tv5h-navy); }
.tv5-page-home .badge-top { background:var(--tv5h-gold); color:var(--tv5h-navy); }
.tv5-page-home .btn-add-flotante { position:absolute; bottom:8px; right:8px; width:44px; height:44px; border-radius:50%; background:rgba(255,255,255,0.95); color:var(--tv5h-navy); border:0; font-size:26px; font-weight:300; cursor:pointer; display:flex; align-items:center; justify-content:center; z-index:2; transition:all 0.2s; box-shadow:0 2px 8px rgba(0,0,0,0.15); }
.tv5-page-home .btn-add-flotante:active { background:var(--tv5h-teal); color:#fff; transform:scale(1.1); }
.tv5-page-home .card-prod-nombre { font-size:12px; font-weight:600; color:var(--tv5h-text); margin-bottom:4px; line-height:1.3; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:32px; }
.tv5-page-home .card-prod-presentacion { font-size:10px; color:var(--tv5h-dim); margin-bottom:8px; }
.tv5-page-home .card-prod-precios { display:flex; align-items:baseline; gap:6px; margin-top:auto; flex-wrap:wrap; }
.tv5-page-home .card-prod-precio { font-size:19px; font-weight:800; color:var(--tv5h-text); }
.tv5-page-home .card-prod-precio-tachado { font-size:11px; color:var(--tv5h-dim); text-decoration:line-through; }
.tv5-page-home .card-prod-off { background:#10B981; color:#04120c; font-size:10.5px; font-weight:900; letter-spacing:0.01em; padding:2px 7px; border-radius:6px; align-self:center; white-space:nowrap; }
.tv5-page-home .card-prod-tag { align-self:flex-start; font-size:9.5px; font-weight:800; letter-spacing:0.06em; text-transform:uppercase; color:var(--tv5h-gold); margin-bottom:2px; }
.tv5-page-home .card-prod-tag.nuevo { color:var(--tv5h-teal); }
.tv5-page-home .card-prod-llega { display:inline-flex; align-items:center; gap:5px; align-self:flex-start; margin-top:10px; background:transparent; color:#34D399; border:1px solid rgba(16,185,129,0.55); font-size:11px; font-weight:700; padding:4px 10px; border-radius:999px; white-space:nowrap; }
.tv5-page-home .card-prod-llega.manana { color:#5BBDD8; border-color:rgba(91,189,216,0.55); }
.tv5-page-home .card-prod-llega-truck { font-size:12px; }
/* GRILLA "Toda la tienda" (Fable): góndola vertical escaneable en vez de carruseles apilados */
.tv5-page-home .grid-titulo { display:flex; align-items:center; gap:8px; font-size:15px; font-weight:800; color:var(--tv5h-text); margin:24px 0 12px; }
.tv5-page-home .grid-titulo .emoji { font-size:16px; }
.tv5-page-home .grid-prod { display:grid; grid-template-columns:repeat(2, 1fr); gap:10px; }
.tv5-page-home .grid-prod .card-prod { min-width:0; width:auto; max-width:none; flex:none; scroll-snap-align:none; }
@media (min-width:640px){ .tv5-page-home .grid-prod { grid-template-columns:repeat(3, 1fr); gap:12px; } }
@media (min-width:900px){ .tv5-page-home .grid-prod { grid-template-columns:repeat(4, 1fr); gap:12px; } }
@media (min-width:1200px){ .tv5-page-home .grid-prod { grid-template-columns:repeat(5, 1fr); gap:14px; } }
.tv5-page-home .grid-vertodo { display:flex; justify-content:center; margin:22px 0 4px; }
.tv5-page-home .grid-seccion { margin-bottom:6px; scroll-margin-top:64px; }
.tv5-page-home .grid-titulo-count { font-size:11px; font-weight:700; color:var(--tv5h-dim); background:rgba(255,255,255,0.06); padding:1px 8px; border-radius:999px; }
@media (hover:hover){ .tv5-page-home .card-prod:hover { transform:translateY(-3px); border-color:var(--tv5h-gold); box-shadow:0 8px 24px rgba(197,160,89,0.15); } .tv5-page-home .btn-add-flotante:hover { background:var(--tv5h-teal); color:#fff; transform:scale(1.1); } }
/* Stepper [− N +] cuando el producto está en el carrito (mismo patrón que Catalogo) */
.tv5-page-home .card-qty-stepper { position:absolute; bottom:8px; right:8px; z-index:2; display:inline-flex; align-items:center; height:34px; background:rgba(255,255,255,0.95); border-radius:999px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.18); }
.tv5-page-home .card-qty-btn { width:30px; height:34px; border:0; background:transparent; color:var(--tv5h-navy); font-size:20px; font-weight:400; line-height:1; cursor:pointer; display:flex; align-items:center; justify-content:center; -webkit-tap-highlight-color:transparent; }
.tv5-page-home .card-qty-btn:active { background:rgba(0,0,0,0.06); }
.tv5-page-home .card-qty-val { min-width:20px; text-align:center; color:var(--tv5h-navy); font-size:14px; font-weight:900; font-variant-numeric:tabular-nums; }
.tv5-page-home .card-prod.en-carrito { border-color:rgba(0,168,181,0.5); box-shadow:0 0 0 1px rgba(0,168,181,0.28); }
@media (hover:hover){ .tv5-page-home .card-qty-btn:hover { color:var(--tv5h-teal); } }
/* Toast "✓ agregado" */
.tv5-page-home .home-toast { position:fixed; bottom:calc(150px + var(--safe-bottom)); left:50%; transform:translateX(-50%) translateY(10px); background:rgba(16,185,129,0.96); color:#04361f; border-radius:999px; padding:12px 20px; font-size:13px; font-weight:800; z-index:130; max-width:calc(100% - 32px); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; box-shadow:0 16px 40px rgba(0,0,0,0.5); opacity:0; pointer-events:none; transition:opacity 0.25s, transform 0.25s; }
.tv5-page-home .home-toast.visible { opacity:1; transform:translateX(-50%) translateY(0); }
@media (min-width:900px){ .tv5-page-home .home-toast { bottom:100px; } }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .home-toast { transition:opacity 0.2s; } }

/* BOTTOM SHEET */
.tv5-page-home .sheet-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.6); backdrop-filter:blur(4px); z-index:200; opacity:0; pointer-events:none; transition:opacity 0.3s; }
.tv5-page-home .sheet-backdrop.abierto { opacity:1; pointer-events:auto; }
.tv5-page-home .sheet { position:fixed; bottom:0; left:0; right:0; z-index:201; background:#142238; border-radius:24px 24px 0 0; border-top:1px solid var(--tv5h-border); padding:8px 20px calc(20px + var(--safe-bottom)); max-height:85vh; overflow-y:auto; transform:translateY(100%); transition:transform 0.35s cubic-bezier(0.32,0.72,0,1); box-shadow:0 -20px 60px rgba(0,0,0,0.5); max-width:520px; margin:0 auto; }
.tv5-page-home .sheet.abierto { transform:translateY(0); }
.tv5-page-home .sheet-handle { width:40px; height:4px; background:var(--tv5h-border); border-radius:2px; margin:8px auto 16px; }
.tv5-page-home .sheet-close { position:absolute; top:12px; right:12px; width:44px; height:44px; border-radius:50%; background:rgba(255,255,255,0.08); border:0; color:var(--tv5h-text); font-size:20px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
.tv5-page-home .sheet-img { aspect-ratio:1; max-height:260px; background:linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%); border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:90px; margin:0 auto 16px; max-width:320px; overflow:hidden; }
.tv5-page-home .sheet-img-foto { width:100%; height:100%; object-fit:cover; display:block; border-radius:14px; }
.tv5-page-home .sheet-nombre { font-size:22px; font-weight:800; letter-spacing:-0.02em; margin-bottom:4px; }
.tv5-page-home .sheet-presentacion { font-size:13px; color:var(--tv5h-muted); margin-bottom:14px; }
.tv5-page-home .sheet-precios { display:flex; align-items:baseline; gap:10px; margin-bottom:20px; }
.tv5-page-home .sheet-precio { font-size:32px; font-weight:800; color:var(--tv5h-text); }
.tv5-page-home .sheet-precio-tachado { font-size:14px; color:var(--tv5h-dim); text-decoration:line-through; }
.tv5-page-home .sheet-descripcion { font-size:13px; color:var(--tv5h-muted); line-height:1.6; margin-bottom:20px; padding:14px; background:rgba(255,255,255,0.03); border-radius:10px; }
.tv5-page-home .sheet-qty { display:flex; align-items:center; gap:12px; margin-bottom:16px; }
.tv5-page-home .sheet-qty-btn { width:44px; height:44px; border-radius:50%; background:rgba(255,255,255,0.08); border:1px solid var(--tv5h-border); color:var(--tv5h-text); font-size:20px; cursor:pointer; }
.tv5-page-home .sheet-qty-num { font-size:18px; font-weight:800; min-width:30px; text-align:center; }
.tv5-page-home .sheet-cta { display:block; width:100%; background:linear-gradient(135deg, var(--tv5h-teal) 0%, #00C4D4 100%); color:var(--tv5h-navy); text-align:center; padding:16px; border-radius:12px; font-weight:800; font-size:15px; text-decoration:none; border:0; cursor:pointer; }
@media (prefers-reduced-motion:reduce){ .tv5-page-home .sheet { transition:none; } .tv5-page-home .sheet-backdrop { transition:none; } }
`;
